import { useCallback } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { SaleApiError } from '../../services/saleLinks';

export function useSaleText() {
  const { currentLanguage } = useTranslation();
  return useCallback((zh: string, en: string): string => currentLanguage === 'en' ? en : zh, [currentLanguage]);
}
export const saleDate = (value?: string | null): string => value ? new Date(value).toLocaleString() : '-';

export type SaleEmbeddedBrowser = 'wechat' | 'qq' | 'qqBrowser' | null;

/** These embedded webviews cannot reliably hand the page off to the system browser. */
export const detectSaleEmbeddedBrowser = (
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): SaleEmbeddedBrowser => {
  const normalized = userAgent.toLowerCase();
  if (normalized.includes('micromessenger')) return 'wechat';
  if (normalized.includes('qqbrowser') || normalized.includes('mqqbrowser')) return 'qqBrowser';
  if (/(?:^|[\s;])qq\//.test(normalized) || normalized.includes('v1_and_sq_') || normalized.includes('v1_iph_sq_')) return 'qq';
  return null;
};

export const isWeChatBrowser = (userAgent?: string): boolean => detectSaleEmbeddedBrowser(userAgent) === 'wechat';

const errors: Record<string, [string, string]> = {
  'account.disabled': ['邮箱账号登录和新账号激活暂未开放，已有授权仍可使用。', 'Email sign-in and new account activations are currently closed. Existing grants remain usable.'],
  'account.mailNotConfigured': ['验证码邮件尚未配置，请联系管理员。', 'Verification email is not configured. Contact the administrator.'],
  'account.temporarilyUnavailable': ['买家账号服务暂不可用，请稍后重试。', 'Buyer account service is temporarily unavailable. Try again later.'],
  'account.rateLimited': ['验证码请求过于频繁或已达发送上限，请稍后重试。', 'Too many email code requests or the sending limit has been reached. Try again later.'],
  'account.mailFailed': ['验证码发送失败，请稍后重新获取。', 'Verification email could not be sent. Request a new code later.'],
  'account.codeInvalid': ['邮箱验证码无效，请检查输入。', 'Invalid email verification code. Check your input.'],
  'account.codeExpired': ['邮箱验证码已过期、已使用或已被新验证码替换，请重新获取。', 'The email code has expired, was used, or was replaced by a newer code. Request a new one.'],
  'account.codeExhausted': ['邮箱验证码尝试次数已用完，请重新获取。', 'Too many incorrect attempts. Request a new email code.'],
  'account.loginRequired': ['请先通过邮箱验证码登录，登录过期后需重新验证。', 'Sign in with an email code. Expired sessions require verification again.'],
  'account.emailInvalid': ['请输入有效的邮箱地址。', 'Enter a valid email address.'],
  'account.bindingConflict': ['激活码已绑定其他设备或账号，不能更改绑定方式。', 'This code is already bound to a device or another account. Its binding cannot be changed.'],
  'account.originInvalid': ['请求来源与当前站点不一致，请在当前页面重试。', 'The request origin does not match this site. Retry on this page.'],
  loginRequired: ['请先登录。', 'Please sign in.'],
  sellerNotEnabled: ['售卖权限尚未开通，请联系管理员。', 'Ask an administrator to enable seller access.'],
  creationDisabled: ['当前暂未开放创建和批量发码。', 'Creation and bulk issuing are currently closed.'],
  activationRequired: ['请输入激活码。', 'Enter your activation code.'],
  boundElsewhere: ['该激活码已绑定其他浏览器，请联系卖家。', 'This code is bound to another browser. Contact the seller.'],
  codeDisabled: ['该激活码已禁用或补发，请联系卖家。', 'This code was disabled or replaced. Contact the seller.'],
  codeInvalid: ['激活码无效，请检查是否属于当前链接。', 'Invalid code. Check that it belongs to this link.'],
  paused: ['卖家已暂停新激活，已绑定的浏览器仍可访问。', 'New activations are paused. Existing browsers retain access.'],
  revoked: ['该售卖链接已撤销。', 'This sale link has been revoked.'],
  resourceUnavailable: ['资源已删除、到期或暂不可用，请联系卖家。', 'The resource is unavailable. Contact the seller.'],
  ownerUnavailable: ['该卖家的资源暂不可用。', 'This seller is currently unavailable.'],
  resourceUrlInvalid: ['资源需要使用有效的 HTTPS 公开地址。', 'The resource needs a valid public HTTPS URL.'],
  temporarilyUnavailable: ['服务暂不可用，请稍后重试。激活失败不会消耗新码。', 'Temporarily unavailable. Retry later; a failed activation does not consume a new code.'],
  rateLimited: ['尝试次数过多，请 10 分钟后重试。', 'Too many attempts. Try again in 10 minutes.'],
  notFound: ['记录不存在或无权访问。', 'Record not found or access denied.'],
  cannotReissue: ['只有已激活或已禁用的码可以补发。', 'Only activated or disabled codes can be replaced.'],
  invalidRequest: ['请检查输入内容和长度。', 'Check the input values and lengths.'],
  batchSizeInvalid: ['生成数量超出允许范围。', 'Batch size is outside the allowed range.'],
  sourceImmutable: ['已创建的售卖链接不能更换关联文件。', 'An existing sale cannot be assigned a different upload.'],
};
export function saleError(error: unknown, text: (zh: string, en: string) => string): string {
  if (error instanceof SaleApiError) {
    const pair = errors[error.code.replace(/^sale\./, '')];
    if (pair) return text(...pair);
    return error.message;
  }
  return text('请求失败，请检查网络后重试。', 'Request failed. Check your connection and retry.');
}

export function saleStatus(status: string, text: (zh: string, en: string) => string): string {
  const labels: Record<string, [string, string]> = {
    ACTIVE: ['正常', 'Active'], PAUSED: ['暂停新激活', 'Activations paused'], REVOKED: ['已撤销', 'Revoked'],
    UNUSED: ['未激活', 'Unused'], ACTIVATED: ['已激活', 'Activated'], DISABLED: ['已禁用', 'Disabled'], REPLACED: ['已补发', 'Replaced'],
  };
  return labels[status] ? text(...labels[status]) : status;
}
